import {
  createContext,
  Fragment,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { IconButton } from './Button';
import { ConfirmDialog } from './ConfirmDialog';
import s from './Drawer.module.css';
import { t } from '@app/i18n';

export interface DrawerProps {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  /**
   * `true` = há alterações não salvas. Esc / clique no scrim / botão fechar pedem
   * confirmação antes de descartar (FR-131).
   */
  dirty?: boolean;
  /** Chamado quando o fechamento é confirmado (ou não havia o que confirmar). */
  onClose: () => void;
  /** Rodapé — um único botão primário (FR-131). */
  footer?: ReactNode;
  children?: ReactNode;
  /** Texto do diálogo de descarte. */
  discardTitle?: string;
  discardMessage?: string;
}

/** Folga do teto da saída sobre a duração da transição: o `transitionend` pode não vir (aba em segundo plano). */
const EXIT_SLACK_MS = 150;

/**
 * Teto do `DrawerPresence` para soltar o painel que sai, se o `Drawer` de dentro nunca avisar o fim: um componente que
 * deixa de desenhar o `Drawer` no meio da saída, por exemplo.
 */
const PRESENCE_CEILING_MS = 1000;

interface Presence {
  /** O filho já saiu da árvore de quem chama: o painel que se vê é a cópia que sai. */
  leaving: boolean;
  /** O `Drawer` avisa que a saída acabou, e a cópia pode sair do DOM. */
  onExited: () => void;
}

const PresenceContext = createContext<Presence | null>(null);

/**
 * Mantém na tela, durante a saída, um drawer que a tela monta só enquanto está aberto (aim-xz9).
 *
 * As telas montam o drawer com uma condição (`{drawer.id ? <UserDrawer open … /> : null}`), e o `open` dele é sempre
 * `true`. Ao fechar, o componente sai da árvore inteiro, e o `Drawer` não tem como animar a saída. O `DrawerPresence`
 * guarda o último filho e o desenha de novo, com as mesmas props, até o `Drawer` de dentro avisar que saiu. A cópia
 * mostra o conteúdo que estava aberto, e não o de um registro vazio. Quem chama não muda: só envolve a condição.
 *
 * Cada abertura depois de um fechamento nasce de novo, como antes do aim-xz9, mesmo durante a saída. A tela que não
 * dá `key` ao drawer (Identidades, Recursos, Auditoria) reaproveitaria o componente que sai, com o estado e o
 * formulário do registro anterior, para o registro que se abriu por cima.
 */
export function DrawerPresence({ children }: { children?: ReactElement | null | false }) {
  const current = isValidElement(children) ? children : null;
  const last = useRef<ReactElement | null>(current);
  if (current) last.current = current;

  const [hadChild, setHadChild] = useState(current !== null);
  const [exiting, setExiting] = useState(false);
  // Muda a cada abertura depois de um fechamento: a chave que faz o filho nascer de novo.
  const [generation, setGeneration] = useState(0);
  if ((current !== null) !== hadChild) {
    setHadChild(current !== null);
    setExiting(current === null);
    if (current !== null) setGeneration((g) => g + 1);
  }

  useEffect(() => {
    if (!exiting) return;
    const ceiling = window.setTimeout(() => setExiting(false), PRESENCE_CEILING_MS);
    return () => window.clearTimeout(ceiling);
  }, [exiting]);

  const leaving = current === null;
  const presence = useMemo<Presence>(() => ({ leaving, onExited: () => setExiting(false) }), [leaving]);
  const shown = current ?? (exiting ? last.current : null);
  return (
    <PresenceContext.Provider value={presence}>
      <Fragment key={generation}>{shown}</Fragment>
    </PresenceContext.Provider>
  );
}

/**
 * A maior duração (com o atraso) da transição do elemento, em ms. Dá 0 sem transição: no jsdom, que não carrega o
 * CSS, e com `prefers-reduced-motion`, que zera as transições (`global.css` e `Drawer.module.css`). Com 0, o painel
 * entra e sai na hora, sem esperar quadro nem evento.
 */
function transitionMs(el: HTMLElement): number {
  const style = window.getComputedStyle(el);
  const ms = (list: string) =>
    list.split(',').map((v) => (parseFloat(v) || 0) * (v.trim().endsWith('ms') ? 1 : 1000));
  const durations = ms(style.transitionDuration);
  const delays = ms(style.transitionDelay);
  return Math.max(0, ...durations.map((d, i) => d + (delays[i] ?? delays[0] ?? 0)));
}

/**
 * Drawer lateral de criar/editar (`.drawer`, FR-131).
 * Fecha por Esc, pelo scrim e pelo × — sempre com confirmação quando `dirty`.
 * Foco vai para o painel ao abrir e volta ao elemento anterior ao fechar.
 *
 * Entra e sai pela direita (aim-xz9). Ao abrir, o painel monta fora da tela e entra no quadro seguinte: montado
 * direto no estado final, o navegador pinta só o fim e a transição não roda. Ao fechar, ele fica montado até a
 * transição acabar (`transitionend`, com o teto de `EXIT_SLACK_MS`), sem receber clique, e só então sai do DOM. O
 * foco volta à página logo no fechamento, sem esperar a saída.
 */
export function Drawer({
  open: openProp,
  title,
  subtitle,
  dirty = false,
  onClose,
  footer,
  children,
  discardTitle = t('common.ui.discardTitle'),
  discardMessage = t('common.ui.discard'),
}: DrawerProps) {
  const presence = useContext(PresenceContext);
  // Dentro de um `DrawerPresence` que já soltou o filho, o painel fecha, mesmo com o `open` que a tela deixou.
  const open = openProp && !presence?.leaving;
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  // `closing`: o `open` já é false e o painel ainda sai. `shown`: o painel está no lugar (a classe `.on`).
  const [wasOpen, setWasOpen] = useState(open);
  const [closing, setClosing] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    setClosing(!open);
  }

  const requestClose = useCallback(() => {
    if (dirty) setConfirmingDiscard(true);
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    if (!open) {
      setConfirmingDiscard(false);
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Sem rolar: o painel ainda está fora da tela, à direita.
    panelRef.current?.focus({ preventScroll: true });
    return () => previouslyFocused.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const panel = panelRef.current;
    if (!panel || transitionMs(panel) === 0) {
      setShown(true);
      return;
    }
    // Dois quadros: o primeiro pinta o painel fora da tela, o segundo o põe no lugar.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [open]);

  useEffect(() => {
    if (!closing) return;
    const panel = panelRef.current;
    const duration = panel ? transitionMs(panel) : 0;
    const unmount = () => {
      setClosing(false);
      presence?.onExited();
    };
    if (!panel || duration === 0) {
      unmount();
      return;
    }
    // O evento borbulha: a transição de um botão dentro do painel também chega aqui, e não é a saída.
    const onEnd = (e: Event) => {
      if (e.target === panel) unmount();
    };
    panel.addEventListener('transitionend', onEnd);
    const ceiling = window.setTimeout(unmount, duration + EXIT_SLACK_MS);
    return () => {
      panel.removeEventListener('transitionend', onEnd);
      window.clearTimeout(ceiling);
    };
  }, [closing, presence]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Com o diálogo de descarte aberto, o Esc é dele.
      if (confirmingDiscard) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, confirmingDiscard, requestClose]);

  /**
   * Prende o Tab dentro do painel enquanto ele está aberto: `aria-modal="true"`
   * promete ao leitor de tela que o resto da página está inerte, e sem isso o
   * foco escapa para os campos atrás — o AT anuncia "modal" e o usuário digita
   * fora dele. Com o diálogo de descarte aberto, o foco é dele.
   */
  useEffect(() => {
    if (!open || confirmingDiscard) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const painel = panelRef.current;
      if (!painel) return;

      const focaveis = Array.from(
        painel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
        // `hidden` cobre a seção recolhida; `offsetParent` não serve — o jsdom não
        // faz layout e devolveria a lista vazia em todo teste.
      ).filter((el) => !el.closest('[hidden]'));

      // Painel sem nada focável: o foco fica nele mesmo, nunca na página atrás.
      if (focaveis.length === 0) {
        e.preventDefault();
        painel.focus();
        return;
      }

      const first = focaveis[0]!;
      const last = focaveis[focaveis.length - 1]!;
      const ativo = document.activeElement;

      if (!painel.contains(ativo)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (ativo === first || ativo === painel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && ativo === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, confirmingDiscard]);

  if (!open && !closing) return null;

  const state = closing ? 'closing' : shown ? 'open' : 'entering';

  return (
    <>
      <div
        className={[s.scrim, shown ? s.scrimOn : '', closing ? s.leaving : ''].filter(Boolean).join(' ')}
        onClick={open ? requestClose : undefined}
        data-testid="drawer-scrim"
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={[s.drawer, shown ? s.on : '', closing ? s.leaving : ''].filter(Boolean).join(' ')}
        // `entering` → `open` → `closing`: o E2E e os testes esperam o painel no lugar por aqui, e não pela classe.
        data-state={state}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        // Drawer e confirmação são ambos `role="dialog" aria-modal`; este testid é
        // como o E2E distingue os dois sem depender da estrutura do DOM.
        data-testid="drawer"
      >
        <div className={s.dh}>
          <h2 id={titleId}>
            {title}
            {subtitle ? <span className={s.subtitle}>{subtitle}</span> : null}
          </h2>
          <IconButton icon="close" label={t('common.ui.close')} onClick={requestClose} />
        </div>
        <div className={s.db}>{children}</div>
        {footer ? <div className={s.df}>{footer}</div> : null}
      </div>

      <ConfirmDialog
        open={confirmingDiscard}
        title={discardTitle}
        message={discardMessage}
        confirmLabel={t('common.ui.discardConfirm')}
        cancelLabel={t('common.ui.keepEditing')}
        destructive
        onCancel={() => setConfirmingDiscard(false)}
        onConfirm={() => {
          setConfirmingDiscard(false);
          onClose();
        }}
      />
    </>
  );
}
